import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SearchSelect from '../components/shared/SearchSelect';
import { EngagementBlockers, engagementBlockReasons } from '../components/workflow/RecordBlockers';
import { Field, FilePicker, InfoTip, inputCls } from '../components/shared/FormKit';
import { FormValidationProvider, useFormValidation } from '../components/shared/formValidation';
import { FormShell, FormSection } from '../components/workflow/FormShell';
import ReviewerSelect from '../components/workflow/ReviewerSelect';
import Qc35ScreenshotExample, { QC35_SCREENSHOT_HINT } from '../components/workflow/Qc35ScreenshotExample';
import { Qc35ChecklistForm } from '../components/workflow/Qc35Checklist';
import { shortEngagementName } from '../components/workflow/CoverageDisplay';
import { engagementTag } from '../utils/engagementName';
import { qc35AnswerGaps, sameQc35Answers } from './qc35Questions';
import { formatDateShort } from '../utils/dateFormat';
import { fetchJSON, API_BASE_URL } from '../utils/api';
import { detailLabel } from '../components/workflow/constants';

// QC35 Form Amendment — submission form (docs/modules/workflow-qc35.md).
// 一間公司一筆（2026-09-03 拍板，同 FSA）：表單沒有 Coverage 區——修改單
// 更正 File archival checklist、CaseWare 截圖與歸檔日；legacy 多案記錄的
// 修剪走 Management 的記錄頁。
//
// 三個更正欄位一律從**記錄現值**起跳（checklist 與歸檔日預填、現行截圖
// 就地列出），改動才算一項更正——要更正一份東西，得先看得到它現在是什麼。
const WorkflowQc35aNew = () => {
  const navigate = useNavigate();

  const [engagements, setEngagements] = useState([]);
  const [engagementId, setEngagementId] = useState('');
  // 更正 checklist：選案時從記錄現值預填，與現值相同就不算更正。
  const [answers, setAnswers] = useState({});
  // QC6/QC7 現況（評級＋EQR）——checklist 元件選案後抓回來，逐題必填要看它。
  const [qcContext, setQcContext] = useState(null);
  // 更正截圖（Form QC35 May 2026 的必附截圖傳錯時的更正路徑）。
  const [screenshot, setScreenshot] = useState(null);
  // 更正歸檔日：選案時預填記錄現值，改了才算一項更正。
  const [archivalDate, setArchivalDate] = useState('');
  const [reason, setReason] = useState('');
  const [reviewerStaffId, setReviewerStaffId] = useState('');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await fetchJSON('/api/workflow/qc35a/options');
        setEngagements(data.engagements || []);
      } catch (e) { setError(e.message); }
    })();
  }, []);

  const picked = engagements.find((e) => String(e.id) === String(engagementId)) || null;
  const recordAnswers = picked?.record?.answers || null;
  // 匯入記錄（上傳簽署表單、checklist 空白）：這張單一定要交出整份 checklist
  // ——同 QCA 對匯入 QC6/QC7 的規則；只換截圖或歸檔日送不出去。
  const legacyRecord = !!picked?.record?.legacyImport;

  // 換案＝換一份表：就地把 checklist 預填成那筆記錄的現值（沒有答案的
  // legacy 記錄預填成空表，填完就是替它補上）。**同步**填，不走 effect——
  // 表單元件自己在掛載時會補預設值，晚一拍蓋回去就會蓋掉那些預設。
  const pickEngagement = (value) => {
    setEngagementId(value);
    const next = engagements.find((e) => String(e.id) === String(value)) || null;
    setAnswers(next?.record?.answers ? { ...next.record.answers } : {});
    setArchivalDate(next?.record?.archivalDate || '');
  };

  // 與記錄現值相同就不是一項更正——後端同一把尺再判一次。
  const checklistChanged = engagementId ? !sameQc35Answers(answers, recordAnswers) : false;
  const dateChanged = !!engagementId && !!archivalDate
    && archivalDate !== (picked?.record?.archivalDate || '');

  // Everything still empty, keyed to the control it belongs to — after the
  // first Submit the page marks those controls and lists them in the banner.
  const gaps = useMemo(() => {
    const g = [];
    if (!engagementId) g.push({ key: 'engagement', label: 'Engagement' });
    if (!reviewerStaffId) g.push({ key: 'reviewerName', label: 'Reviewer' });
    if (!reason.trim()) g.push({ key: 'reason', label: 'Reason' });
    // 動過 checklist（或記錄本來就沒有 checklist）就得交出一份完整的表——
    // 修改單不把記錄改成半填的表。
    if (engagementId && (checklistChanged || legacyRecord)) g.push(...qc35AnswerGaps(answers, qcContext));
    return g;
  }, [engagementId, reviewerStaffId, reason, checklistChanged, legacyRecord, answers, qcContext]);
  const validation = useFormValidation(gaps);

  const submit = async () => {
    if (!validation.validate()) { setError(null); return; }
    setError(null);
    // 一定要有東西可更正：改 checklist、修正截圖或更正歸檔日至少一項。
    if (!checklistChanged && !legacyRecord && !screenshot && !dateChanged) {
      setError('Correct the File archival checklist, upload a corrected CaseWare screenshot, or correct the archival date.');
      return;
    }
    setSaving(true);
    try {
      const body = new FormData();
      body.set('engagementId', parseInt(engagementId, 10));
      body.set('reason', reason.trim());
      body.set('reviewerStaffId', reviewerStaffId);
      if (dateChanged) body.set('archivalDate', archivalDate);
      if (checklistChanged || legacyRecord) body.set('answers', JSON.stringify(answers));
      if (screenshot) body.set('screenshot', screenshot);
      const data = await fetchJSON('/api/workflow/qc35a', { method: 'POST', body });
      navigate(`/workflow/qc35a/${data.request.id}`);
    } catch (e) { setError(e.message); setSaving(false); }
  };

  return (
    <FormShell
      backTo="/workflow/qc35a"
      backLabel="QC35 Form Amendment"
      title="New QC35 Form Amendment Request"
      subtitle="Correct an approved QC35 Form."
      error={error || validation.error}
      onSubmit={submit}
      submitting={saving}
      submitDisabled={engagementBlockReasons(picked ? [picked] : [], 'qc35a').length > 0}
    >
      <FormValidationProvider value={validation.providerValue}>
        <FormSection step={1} title="Select the Engagement">
          <Field label="Engagement" required field="engagement" hint="Only engagements with a QC35 Form record are listed.">
            <SearchSelect
              value={engagementId}
              onChange={pickEngagement}
              options={engagements.map((e) => ({ value: e.id, label: [`${shortEngagementName(e.engagementName)} — FYE ${formatDateShort(e.yearEnd)}`, engagementTag(e.serviceProvided, e.engagementSeq, e.serviceDescription)].filter(Boolean).join(' · ') }))}
              placeholder="Search engagement…"
            />
            <EngagementBlockers selected={picked ? [picked] : []} flow="qc35a" />
          </Field>
        </FormSection>

        <FormSection step={2} title="Corrections">
          {!engagementId ? (
            <p className="text-sm text-slate-500">Select an engagement above first.</p>
          ) : (
            <>
              {legacyRecord && (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                  This record was imported from a signed form — its File archival checklist is empty.
                  Complete the whole checklist here; on approval the record becomes a fully answered one and the
                  uploaded signed form stays on file.
                </p>
              )}
              {/* 記錄現有的 checklist 就地可改——這張表就是 QC35 本身。
                  三面共用同一份題庫與元件（New／Modify／Management 記錄）。 */}
              <Qc35ChecklistForm
                answers={answers}
                onChange={setAnswers}
                engagements={picked ? [picked] : []}
                onContext={setQcContext}
              />
              <p className="text-xs text-slate-500">
                {legacyRecord
                  ? 'The whole checklist is required for an imported record.'
                  : checklistChanged
                    ? 'The checklist above will be corrected on approval.'
                    : 'Edit any answer above to correct the checklist, or leave it as it is.'}
              </p>
              {/* 現行截圖：檔案輸入框沒辦法預填，但至少要看得到記錄上現在
                  掛的是哪一張——不然「要不要換」無從判斷。 */}
              {picked?.record?.screenshot && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                  <span className={`${detailLabel} mr-2`}>Current CaseWare Screenshot on File</span>
                  <a
                    href={`${API_BASE_URL}/api/my/engagements/${picked.id}/attachments/qc35/${picked.record.screenshot.fileId}`}
                    className="text-blue-600 hover:text-blue-800 underline"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {picked.record.screenshot.originalName}
                  </a>
                  {picked.record.screenshot.requestNo && (
                    <span className="text-xs text-slate-500 ml-2">({picked.record.screenshot.requestNo})</span>
                  )}
                </div>
              )}
              <Field label="Corrected CaseWare Screenshot" hint={<>{QC35_SCREENSHOT_HINT} <InfoTip text="Uploading replaces the record's current screenshot. Leave empty to keep it." /></>}>
                <FilePicker
                  accept=".png,.jpg,.jpeg,.gif,.webp"
                  onChange={(e) => setScreenshot(e.target.files?.[0] || null)}
                  files={screenshot}
                />
                <Qc35ScreenshotExample />
              </Field>
              {/* 歸檔日住在記錄上——更正它就是更正全部涵蓋案的歸檔日。 */}
              <Field
                label="Corrected Archival Date"
                hint={<>{dateChanged
                  ? 'The archival date will be corrected on approval.'
                  : "Pre-filled with the record's current archival date — change it to correct it."} <InfoTip text="It cannot be before the sign-off date. Changing it changes every covered engagement." /></>}
              >
                <input type="date" value={archivalDate} onChange={(e) => setArchivalDate(e.target.value)} className={inputCls} />
              </Field>
            </>
          )}
        </FormSection>

        <FormSection step={3} title="Reason">
          <Field label="Reason" required field="reason">
            <textarea rows="2" value={reason} onChange={(e) => setReason(e.target.value)} className={inputCls} />
          </Field>
        </FormSection>

        <FormSection step={4} title="Reviewer">
          <Field label="Reviewer" required field="reviewerName"
            hint="You pick who reviews this amendment, from the firm's Partners.">
            <ReviewerSelect value={reviewerStaffId} onChange={setReviewerStaffId} grade="partner" />
          </Field>
        </FormSection>
      </FormValidationProvider>
    </FormShell>
  );
};

export default WorkflowQc35aNew;
